import { useCallback, useEffect, useReducer, useRef, useState } from "react";

interface Coordinates {
  x: number;
  y: number;
}

export interface ViewportBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

interface OffsetPixelCoordinates {
  offsetX: number;
  offsetY: number;
}

interface Zoom {
  deltaY: number;
}

export enum ActionTypes {
  PAN = "PAN",
  PAN_START = "PAN_START",
  PAN_END = "PAN_END",
  ZOOM = "ZOOM",
  RESET_ORIGIN = "RESET_ORIGIN",
  FOCUS = "FOCUS",
}

interface PanStartAction {
  type: ActionTypes.PAN_START;
  payload: OffsetPixelCoordinates;
}

interface PanAction {
  type: ActionTypes.PAN;
  payload: OffsetPixelCoordinates;
}

interface PanEndAction {
  type: ActionTypes.PAN_END;
  payload: OffsetPixelCoordinates;
}

interface ZoomAction {
  type: ActionTypes.ZOOM;
  payload: OffsetPixelCoordinates & Zoom;
}

interface ResetOriginAction {
  type: ActionTypes.RESET_ORIGIN;
  payload: Coordinates;
}

interface FocusAction {
  type: ActionTypes.FOCUS;
  payload: {
    point: Coordinates;
    viewWidth: number;
    viewHeight: number;
    // Max visible width (in svg coordinate units) below which the view is
    // considered "too far out" and a fallback zoom-in kicks in. Callers that
    // think in real-world mm must convert with the drawing's mmScaleFactor.
    maxViewWidthSvgUnits?: number;
    // When provided, the view is left untouched if these bounds already fit
    // entirely within the current viewport (everything is already visible).
    contentBounds?: ViewportBounds;
  };
}

type Action =
  | PanStartAction
  | PanAction
  | PanEndAction
  | ZoomAction
  | ResetOriginAction
  | FocusAction;

export interface State {
  origin: Coordinates;
  prevScaledOffset: Coordinates;
  scale: number;
  isDragging: boolean;
  dragStartPos: Coordinates | null;
}

export interface DrawingSettings {
  origin: Coordinates;
  scale: number;
  enableLeftClickDrag?: boolean;
}

// MIN & MAX SCALE are fixed but might worth having them
// as parameters to zooming & panning.
export const MIN_SCALE = 0.005;
export const MAX_SCALE = 1.5;
const WHEEL_DELTA_Y_TO_DELTA_SCALE = 0.0001;

const wheelDeltaYToScale = (scale: number, deltaY: number): number => {
  const newWheelScale = scale - deltaY * WHEEL_DELTA_Y_TO_DELTA_SCALE;
  return Math.min(Math.max(newWheelScale, MIN_SCALE), MAX_SCALE);
};

const LEFT_MOUSE_CLICK_EVENT = 0;
const MIDDLE_MOUSE_CLICK_EVENT = 1;
const RIGHT_MOUSE_CLICK_EVENT = 2;

const isRightOrMiddleMouseClick = (e: MouseEvent): boolean => {
  return (
    e.button === RIGHT_MOUSE_CLICK_EVENT ||
    e.button === MIDDLE_MOUSE_CLICK_EVENT
  );
};

const isLeftMouseClick = (e: MouseEvent): boolean => {
  return e.button === LEFT_MOUSE_CLICK_EVENT;
};

export const panAndZoomReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionTypes.PAN_START: {
      const scaledOffsetX = action.payload.offsetX / state.scale;
      const scaledOffsetY = action.payload.offsetY / state.scale;
      return {
        ...state,
        prevScaledOffset: {
          x: scaledOffsetX,
          y: scaledOffsetY,
        },
        isDragging: true,
        dragStartPos: {
          x: action.payload.offsetX,
          y: action.payload.offsetY,
        },
      };
    }
    case ActionTypes.PAN: {
      const scaledOffsetX = action.payload.offsetX / state.scale;
      const scaledOffsetY = action.payload.offsetY / state.scale;
      return {
        ...state,
        origin: {
          x: state.prevScaledOffset.x - scaledOffsetX + state.origin.x,
          y: state.prevScaledOffset.y - scaledOffsetY + state.origin.y,
        },
        prevScaledOffset: {
          x: scaledOffsetX,
          y: scaledOffsetY,
        },
      };
    }
    case ActionTypes.PAN_END:
      return {
        ...state,
        isDragging: false,
        dragStartPos: null,
      };
    case ActionTypes.ZOOM: {
      const scaledOffsetX = action.payload.offsetX / state.scale;
      const scaledOffsetY = action.payload.offsetY / state.scale;
      const newScale = wheelDeltaYToScale(state.scale, action.payload.deltaY);
      const newScaledOffsetX = action.payload.offsetX / newScale;
      const newScaledOffsetY = action.payload.offsetY / newScale;
      return {
        ...state,
        scale: newScale,
        origin: {
          x: state.origin.x + scaledOffsetX - newScaledOffsetX,
          y: state.origin.y + scaledOffsetY - newScaledOffsetY,
        },
      };
    }
    case ActionTypes.RESET_ORIGIN:
      return {
        ...state,
        origin: action.payload,
      };
    case ActionTypes.FOCUS: {
      const {
        point,
        viewWidth,
        viewHeight,
        maxViewWidthSvgUnits,
        contentBounds,
      } = action.payload;
      // If everything (all scene markers) already fits within the current
      // viewport, the target is on screen — don't move or zoom on selection.
      if (contentBounds) {
        const left = state.origin.x;
        const top = state.origin.y;
        const right = left + viewWidth / state.scale;
        const bottom = top + viewHeight / state.scale;
        const allVisible =
          contentBounds.minX >= left &&
          contentBounds.maxX <= right &&
          contentBounds.minY >= top &&
          contentBounds.maxY <= bottom;
        if (allVisible) {
          return state;
        }
      }
      // Only zoom in when the current view is more zoomed out than the target
      // area (so far out the scene wouldn't be clearly visible); otherwise keep
      // the user's current zoom and just re-center on the point.
      const minScale = maxViewWidthSvgUnits
        ? viewWidth / maxViewWidthSvgUnits
        : 0;
      const scale = Math.min(
        Math.max(state.scale, minScale, MIN_SCALE),
        MAX_SCALE,
      );
      // viewWidth/viewHeight are the visible viewport in screen px. Because the
      // svg renders 1 screen px per (1 / scale) coordinate units, the visible
      // coordinate span is viewSize / scale — center the point within it.
      return {
        ...state,
        scale,
        origin: {
          x: point.x - viewWidth / scale / 2,
          y: point.y - viewHeight / scale / 2,
        },
      };
    }
    default:
      return state;
  }
};

const defaultState: State = {
  origin: {
    x: 0,
    y: 0,
  },
  prevScaledOffset: {
    x: 0,
    y: 0,
  },
  scale: 1.0,
  isDragging: false,
  dragStartPos: null,
};

export const usePanAndZoom = (initialSettings?: DrawingSettings) => {
  const [state, dispatch] = useReducer(panAndZoomReducer, {
    ...defaultState,
    ...initialSettings,
  });
  const [refMounted, setRefMounted] = useState<boolean>(false);
  const ref = useRef<SVGSVGElement | null>(null);
  const enableLeftClickDrag = initialSettings?.enableLeftClickDrag ?? false;

  const containerRef = useCallback((element: SVGSVGElement) => {
    ref.current = element;
    setRefMounted(true);
  }, []);

  const onMouseMoveInWindow = (e: MouseEvent) => {
    e.preventDefault();
    dispatch({
      type: ActionTypes.PAN,
      payload: { offsetX: e.offsetX, offsetY: e.offsetY },
    });
  };

  const onMouseDownInWindow = useCallback(
    (e: MouseEvent) => {
      if (
        isRightOrMiddleMouseClick(e) ||
        (enableLeftClickDrag && isLeftMouseClick(e))
      ) {
        dispatch({
          type: ActionTypes.PAN_START,
          payload: {
            offsetX: e.offsetX,
            offsetY: e.offsetY,
          },
        });
        if (ref.current) {
          ref.current.addEventListener("mousemove", onMouseMoveInWindow);
        }
      }
    },
    [enableLeftClickDrag],
  );

  const onMouseUpInWindow = useCallback(
    (e: MouseEvent) => {
      if (
        ref.current &&
        (isRightOrMiddleMouseClick(e) ||
          (enableLeftClickDrag && isLeftMouseClick(e)))
      ) {
        ref.current.removeEventListener("mousemove", onMouseMoveInWindow);
        dispatch({
          type: ActionTypes.PAN_END,
          payload: {
            offsetX: e.offsetX,
            offsetY: e.offsetY,
          },
        });
      }
    },
    [enableLeftClickDrag],
  );

  const onWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.deltaY !== 0 && ref.current) {
      dispatch({
        type: ActionTypes.ZOOM,
        payload: {
          deltaY: e.deltaY,
          offsetX: e.offsetX,
          offsetY: e.offsetY,
        },
      });
    }
  }, []);

  useEffect(() => {
    if (refMounted && ref.current) {
      ref.current.addEventListener("mousedown", onMouseDownInWindow);
      ref.current.addEventListener("mouseup", onMouseUpInWindow);
      ref.current.addEventListener("wheel", onWheel, {
        passive: false,
      });
    }
    return () => {
      if (refMounted && ref.current) {
        ref.current.removeEventListener("mousedown", onMouseDownInWindow);
        ref.current.removeEventListener("mouseup", onMouseUpInWindow);
        ref.current.removeEventListener("wheel", onWheel);
      }
    };
  }, [refMounted, onWheel, onMouseUpInWindow, onMouseDownInWindow]);

  const resetOrigin = useCallback((newOrigin: Coordinates) => {
    dispatch({
      type: ActionTypes.RESET_ORIGIN,
      payload: newOrigin,
    });
  }, []);

  const focusOnPoint = useCallback(
    (
      point: Coordinates,
      options?: {
        maxViewWidthSvgUnits?: number;
        contentBounds?: ViewportBounds;
      },
    ) => {
      const element = ref.current;
      if (!element) {
        return;
      }
      // The svg is far larger than what's on screen; the visible viewport is its
      // (overflow-clipping) parent. Center using that real size so the point
      // lands in the middle of what the user actually sees.
      const viewport = element.parentElement ?? element;
      const rect = viewport.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        return;
      }
      dispatch({
        type: ActionTypes.FOCUS,
        payload: {
          point,
          viewWidth: rect.width,
          viewHeight: rect.height,
          maxViewWidthSvgUnits: options?.maxViewWidthSvgUnits,
          contentBounds: options?.contentBounds,
        },
      });
    },
    [],
  );

  return {
    ...state,
    containerRef,
    resetOrigin,
    focusOnPoint,
  };
};
